import axios, { create, AxiosInstance, AxiosResponse, AxiosError, InternalAxiosRequestConfig } from "axios";

import tokenHandler from "./tokenHandler";
import { getCookie } from "./cookies";
import API from "../constants/api";
import { CSRF_COOKIE_NAME, CSRF_HEADER_NAME } from "../constants/cookies";
import { NameOfChildRoutes, NamesOfParentRoutes } from "../constants/routes";

axios.defaults.headers.post["Content-Type"] = "application/json";

// `withCredentials` so the http-only refresh cookie and the JS-readable CSRF cookie flow
// on cross-origin requests (web and API are same-site but different origins).
const axiosInstance: AxiosInstance = create({
  withCredentials: true,
  // Axios otherwise emits its default `X-XSRF-TOKEN` header whenever
  // `withCredentials` is enabled. The API's CORS contract and CSRF middleware use
  // `X-CSRF-Token`; keeping Axios on that same name prevents browsers from rejecting
  // the preflight before the login request is sent.
  xsrfCookieName: CSRF_COOKIE_NAME,
  xsrfHeaderName: CSRF_HEADER_NAME,
});

// The API enforces CSRF double-submit only on state-changing methods (GET/HEAD/OPTIONS are exempt).
const MUTATING_METHODS = ["post", "put", "patch", "delete"] as const;
type MutatingMethod = (typeof MUTATING_METHODS)[number];

const isMutatingMethod = (method?: string): method is MutatingMethod =>
  (MUTATING_METHODS as readonly string[]).includes((method ?? "").toLowerCase());

// Endpoints that must never trigger the silent-refresh retry — they ARE the auth flow
// (refreshing on their 401 would recurse). Full absolute URLs (the app builds them from baseApiUrl).
const AUTH_ENDPOINTS: readonly string[] = [
  API.AUTH.LOGIN,
  API.AUTH.REFRESH,
  API.AUTH.LOGOUT,
  API.AUTH.GOOGLE_LOGIN,
  API.AUTH.GOOGLE_REGISTER,
];

const LOGIN_PATH = `${NamesOfParentRoutes.AUTH}${NameOfChildRoutes.AUTH.LOGIN}`;

// Fail-closed bound on the refresh call: a hung /auth/refresh must not strand the bootstrap
// loader (App.tsx) forever. Scoped to refresh only (below) so long domain requests aren't capped.
export const REFRESH_TIMEOUT_MS = 15000;

interface RetryConfig extends InternalAxiosRequestConfig {
  _retry?: boolean;
}

// --- Cross-tab refresh serialization (Web Locks) ---
// Single-flight is per-tab; two tabs refreshing the same cookie at once make the API's rotation
// CAS revoke the whole session (deliberate reuse-detection). A shared named lock serializes every
// /auth/refresh across tabs so each uses the then-current cookie (v1→v2→v3), never colliding.
// One shared, stable lock name across all tabs and both refresh callers — this constant IS the
// cross-tab serialization contract; a per-call/random name would silently defeat it.
export const AUTH_REFRESH_LOCK_NAME = "923core-auth-refresh";

type LockManagerLike = { request: <T>(name: string, callback: () => Promise<T>) => Promise<T> };
const getLockManager = (): LockManagerLike | undefined =>
  typeof navigator !== "undefined" ? (navigator as unknown as { locks?: LockManagerLike }).locks : undefined;

/**
 * Run a refresh call under a shared cross-tab Web Lock so concurrent /auth/refresh calls from
 * multiple tabs are serialized (each uses the then-current rotated cookie), avoiding the API's
 * concurrent-rotation reuse-detection cascade. Falls back to running `fn` directly where the
 * Web Locks API is unavailable (older browser / non-secure context) — degrades to per-tab
 * single-flight, no regression.
 *
 * @param fn The refresh operation to serialize.
 * @returns The result of `fn`.
 */
export const withAuthRefreshLock = <T,>(fn: () => Promise<T>): Promise<T> => {
  const locks = getLockManager();
  return locks ? locks.request(AUTH_REFRESH_LOCK_NAME, fn) : fn();
};

/**
 * Request interceptor: attach the bearer access token (all methods) and, on mutating
 * methods, the CSRF header echoed from the cookie. Read FRESH per request so a retry
 * after a silent refresh picks up the new access token AND the rotated CSRF cookie.
 */
export const attachAuthHeaders = (config: InternalAxiosRequestConfig): InternalAxiosRequestConfig => {
  const token = tokenHandler.get();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  if (isMutatingMethod(config.method)) {
    const csrfToken = getCookie(CSRF_COOKIE_NAME);
    if (csrfToken) {
      config.headers[CSRF_HEADER_NAME] = csrfToken;
    }
  }
  if (config.url === API.AUTH.REFRESH) {
    config.timeout = REFRESH_TIMEOUT_MS;
  }
  return config;
};

axiosInstance.interceptors.request.use(attachAuthHeaders);

// --- Silent single-flight refresh on 401 (ported from the web boilerplate's request.ts) ---

let isRefreshing = false;
let refreshPromise: Promise<string> | null = null;
let requestQueue: { resolve: (value: unknown) => void; reject: (reason: unknown) => void; config: RetryConfig }[] = [];

const startRefreshToken = async (): Promise<string> => {
  const { token } = (await withAuthRefreshLock(() => axiosInstance.post(API.AUTH.REFRESH))) as { token?: string };
  // A 2xx without a token is not a usable refresh: treat it as a failure so we end the
  // session (clear + redirect) instead of silently retrying with the stale token.
  if (!token) {
    throw new Error("No token in refresh response");
  }
  tokenHandler.set(token);
  return token;
};

// Replay (or reject) the requests that queued while a refresh was in flight.
const processQueue = (error: unknown, refreshed: boolean) => {
  const queue = requestQueue;
  requestQueue = [];
  queue.forEach(({ resolve, reject, config }) => {
    if (refreshed) {
      config._retry = true;
      resolve(axiosInstance(config));
    } else {
      reject(error);
    }
  });
};

// Refresh failed → the session is over: reject anything queued, drop the token, and hard-redirect
// to login. A full reload also re-seeds redux `authentificated` from the (now empty) token, so we
// avoid importing the store here (no cycle). We do NOT call server /auth/logout — it needs a valid
// access token we no longer have (would 403).
const endSession = (error: unknown) => {
  processQueue(error, false);
  tokenHandler.remove();
  window.location.href = LOGIN_PATH;
};

/**
 * Runs the shared single-flight, cross-tab-locked access-token refresh: joins an already
 * in-flight refresh instead of starting a second one, replays any axios requests that queued
 * behind it once it succeeds, and ends the session (see `endSession`) if it fails. Both the
 * axios response interceptor below and any non-axios caller (the chat answer stream's own
 * `fetch`, which gets no axios interceptor) share this one function so there is exactly one
 * refresh mechanism, never a duplicated one.
 *
 * @returns The new access token.
 * @throws The refresh error, after the session has already been ended.
 */
export const refreshAccessToken = async (): Promise<string> => {
  if (isRefreshing && refreshPromise) {
    return refreshPromise;
  }

  isRefreshing = true;
  refreshPromise = startRefreshToken();

  try {
    const token = await refreshPromise;
    processQueue(null, true);
    return token;
  } catch (error) {
    endSession(error);
    throw error;
  } finally {
    isRefreshing = false;
    refreshPromise = null;
  }
};

axiosInstance.interceptors.response.use(
  (res: AxiosResponse) => {
    const response = res.data || res;
    if (response.error) {
      return Promise.reject(response.error);
    }
    return response;
  },
  async (error: AxiosError) => {
    const originalRequest = error.config as RetryConfig | undefined;
    const isAuthEndpoint = !!originalRequest && AUTH_ENDPOINTS.includes(originalRequest.url ?? "");
    const shouldRefresh =
      error.response?.status === 401 &&
      !!originalRequest &&
      !isAuthEndpoint &&
      !originalRequest._retry &&
      !!tokenHandler.get();

    if (shouldRefresh && originalRequest) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => requestQueue.push({ resolve, reject, config: originalRequest }));
      }

      originalRequest._retry = true;

      try {
        await refreshAccessToken();
        return axiosInstance(originalRequest);
      } catch (refreshError) {
        return Promise.reject(refreshError);
      }
    }

    const { response } = error || {};
    const { data } = response || {};
    if (data) {
      return Promise.reject(data);
    }

    return Promise.reject(error);
  },
);

const makeRequest = (instance: AxiosInstance) => (method: string, url: string, params: unknown[]) =>
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  instance[method](url, ...params);

/**
 * Axios wrapper
 *
 * @param  {string} method Method of the request
 * @param  {string} url url of the request
 *
 * @return {object} wrapped axios function that receives params
 */
export default (method: string, url: string) =>
  (...params: unknown[]) =>
    makeRequest(axiosInstance)(method, url, params);
